import { useState } from "react";
import axios from "axios";
import { ArrowDown, Loader2, Wand2 } from "lucide-react";

import { API } from "@/lib/api";

export const SmartTriage = ({ onApply }) => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    if (text.trim().length < 10 || loading) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.post(`${API}/ai/triage`, { text: text.trim() });
      setResult(res.data);
    } catch (e) {
      setError("Η ανάλυση δεν ολοκληρώθηκε. Μπορείτε να συμπληρώσετε τη φόρμα απευθείας.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="smart-triage" className="relative overflow-hidden rounded-[32px] mesh-copper border border-[#C26D52]/20 p-8 lg:p-10 mb-8 shadow-[0_30px_70px_-50px_rgba(194,109,82,0.5)]">
      <p className="inline-flex items-center gap-2 rounded-full bg-[#C26D52] px-3.5 py-1.5 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-white">
        <Wand2 className="h-3.5 w-3.5" /> Έξυπνη Βοήθεια με AI
      </p>
      <h3 className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-[#0E1B31] leading-snug">
        Περιγράψτε ελεύθερα τι χρειάζεστε
      </h3>
      <p className="mt-2 text-sm text-[#3B4A5E] leading-relaxed">
        Το AI θα προτείνει τον καταλληλότερο άξονα υπηρεσιών και θα συντάξει ένα πρώτο προσχέδιο
        μηνύματος για εσάς.
      </p>
      <textarea
        data-testid="triage-input"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="π.χ. Έχουμε μια μικρή μονάδα παραγωγής και θέλουμε να ξεκινήσουμε εξαγωγές, αλλά δεν ξέρουμε από πού να αρχίσουμε..."
        className="mt-5 w-full rounded-2xl bg-white border border-[#0E1B31]/10 px-4 py-3.5 text-sm text-[#0E1B31] placeholder:text-[#6B7A8F]/60 focus:outline-none focus:border-[#C26D52] focus:shadow-[0_0_0_4px_rgba(194,109,82,0.1)] transition-all resize-y"
      />
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <button
          data-testid="triage-button"
          onClick={run}
          disabled={loading || text.trim().length < 10}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#C26D52] text-white text-sm font-semibold hover:bg-[#A9583F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 shadow-[0_14px_30px_-14px_rgba(194,109,82,0.7)]"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {loading ? "Ανάλυση..." : "Ανάλυση & Σύνταξη"}
        </button>
        {error && <p data-testid="triage-error" className="text-xs text-red-700">{error}</p>}
      </div>

      {result && (
        <div data-testid="triage-result" className="mt-6 rounded-[24px] glass p-6">
          {result.interest && (
            <p className="text-sm text-[#3B4A5E]">
              Προτεινόμενος άξονας:{" "}
              <span className="font-semibold text-[#C26D52]" data-testid="triage-interest">
                {result.interest}
              </span>
            </p>
          )}
          {result.summary && (
            <p className="mt-3 text-sm text-[#3B4A5E] leading-relaxed italic border-l-2 border-[#C26D52] pl-4">
              «{result.summary}»
            </p>
          )}
          <button
            data-testid="triage-apply-button"
            onClick={() => onApply({ interest: result.interest, message: result.summary })}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#0E1B31] text-[#F8F6F0] text-xs font-semibold hover:bg-[#2C4A6F] transition-colors duration-300"
          >
            <ArrowDown className="h-3.5 w-3.5" /> Μεταφορά στη φόρμα
          </button>
        </div>
      )}
    </div>
  );
};
